import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { PrintStateEnum } from "@/apis/purchase";
import type { BatchCompleteFilters } from "./useBatchCompleteTable";
import {
  PurchasefInStateEnum,
  PurchaseOrderItem,
  PurchaseOrderStateEnum,
} from "@/types/purchase";
import {
  App,
  Button,
  ButtonProps,
  DatePicker,
  Flex,
  Input,
  Modal,
  ModalProps,
  Select,
  Tooltip,
  Typography,
} from "antd";
import SupplierSelect from "@/pages/Purchase/components/SupplierSelect";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { AnyObject } from "antd/lib/_util/type";
import { ColumnsType } from "antd/lib/table";
import { FC, Key, MutableRefObject, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;

interface BatchCompleteTableBaseProps
  extends Omit<ButtonProps, "onClick" | "title"> {
  open: boolean;
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => void;
  modalProps?: Omit<ModalProps, "open" | "onOk" | "onCancel" | "title">;
  confirmLoading?: boolean;
  modalTitle?: ReactNode;
}

export interface BatchCompleteTableProps extends BatchCompleteTableBaseProps {
  tableLayoutRef: MutableRefObject<TableLayoutRef | null>;
  columns: ColumnsType<PurchaseOrderItem>;
  dataSource: PurchaseOrderItem[];
  total: number;
  tableLoading?: boolean;
  selectedRowKeys: Key[];
  filters: BatchCompleteFilters;
  searchFilters: BatchCompleteFilters;
  onOpen?: () => void;
  onPageChange?: (offset: number, pageSize: number) => void;
  onSelectedRowKeysChange?: (keys: Key[], rows: PurchaseOrderItem[]) => void;
  onFiltersChange?: (value: BatchCompleteFilters) => void;
  onSearch?: (value?: BatchCompleteFilters) => void;
  onClearFilters?: () => void;
}

const BatchCompleteTable: FC<BatchCompleteTableProps> = ({
  open,
  tableLayoutRef: tableRef,
  columns,
  dataSource,
  total,
  tableLoading = false,
  selectedRowKeys,
  filters,
  searchFilters,
  onOpen,
  onPageChange,
  onSelectedRowKeysChange,
  onFiltersChange,
  onSearch,
  onClearFilters,
  onConfirm,
  onCancel,
  modalProps,
  confirmLoading,
  modalTitle,
  children,
  disabled,
  type,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [innerConfirmLoading, setInnerConfirmLoading] = useState(false);

  const availableCount = total ?? dataSource.length;

  const handleConfirm = async () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({
        placement: "top",
        message: t("common.selectLeastOne"),
      });
      return;
    }

    try {
      setInnerConfirmLoading(true);
      await onConfirm?.();
    } finally {
      setInnerConfirmLoading(false);
    }
  };

  return (
    <>
      <Button
        type={type ?? "primary"}
        disabled={disabled}
        onClick={onOpen}
        {...buttonProps}
      >
        {children ?? t("purchase.batchCompletedProduction")}
      </Button>
      <Modal
        title={
          modalTitle ?? (
            <div>
              {t("purchase.batchCompletedProduction")}{" "}
              <Typography.Text>
                {t("purchase.batchCompleteSummary", {
                  selected: selectedRowKeys.length,
                  available: availableCount,
                })}
              </Typography.Text>
            </div>
          )
        }
        open={open}
        width={1200}
        onOk={handleConfirm}
        onCancel={onCancel}
        confirmLoading={confirmLoading ?? innerConfirmLoading}
        okText={t("common.confirm")}
        cancelText={t("common.cancel")}
        destroyOnClose
        {...modalProps}
      >
        <div style={{ height: 650, display: "flex", flexDirection: "column" }}>
          <STableLayout
            key={`batch-complete-${String(searchFilters.printState ?? "all")}-${String(
              searchFilters.orderState ?? "all",
            )}-${String(searchFilters.inState ?? "all")}`}
            loading={tableLoading}
            dataSource={dataSource}
            columns={columns as ColumnsType<AnyObject>}
            rowKey="id"
            total={availableCount}
            tableLayoutRef={tableRef}
            handleChange={(offset) =>
              onPageChange?.(
                offset,
                tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
              )
            }
            headerChildren={
              <Flex vertical style={{ width: "100%", gap: 12 }}>
                <Flex
                  align="center"
                  style={{ width: "100%", gap: 12, flexWrap: "wrap" }}
                >
                  <Select
                    allowClear
                    value={filters.printState}
                    placeholder={t("purchase.printState")}
                    style={{ width: 130 }}
                    onChange={(value) => {
                      const nextFilters = {
                        ...filters,
                        printState: value as PrintStateEnum,
                      };
                      onFiltersChange?.(nextFilters);
                    }}
                    options={[
                      {
                        label: t("purchase.unPrt"),
                        value: PrintStateEnum.UN_PRINT,
                      },
                      {
                        label: t("purchase.prt"),
                        value: PrintStateEnum.PRINTED,
                      },
                    ]}
                  />
                  <Select
                    allowClear
                    value={filters.inState}
                    placeholder={t("purchase.inState")}
                    style={{ width: 130 }}
                    onChange={(value) => {
                      const nextFilters = {
                        ...filters,
                        inState: value,
                      };
                      onFiltersChange?.(nextFilters);
                    }}
                    options={[
                      {
                        label: t("purchase.unArrived"),
                        value: PurchasefInStateEnum.UN_ARRIVED,
                      },
                      {
                        label: t("purchase.partialIn"),
                        value: PurchasefInStateEnum.PARTIAL_IN,
                      },
                      {
                        label: t("purchase.allIn"),
                        value: PurchasefInStateEnum.ALL_IN,
                      },
                    ]}
                  />
                  {/* <Select
                    allowClear
                    value={filters.finishState}
                    placeholder={t("purchase.deliveryStatus")}
                    style={{ width: 130 }}
                    onChange={(value) => {
                      const nextFilters = {
                        ...filters,
                        finishState: value,
                      };
                      onFiltersChange?.(nextFilters);
                    }}
                    options={[
                      {
                        label: t("purchase.pendingProduction"),
                        value: PurchasefFnishStateEnum.UN_FINISHED,
                      },
                      {
                        label: t("purchase.inProduction"),
                        value: PurchasefFnishStateEnum.FINISHING,
                      },
                      {
                        label: t("purchase.finishedProduction"),
                        value: PurchasefFnishStateEnum.FINISHED,
                      },
                    ]}
                  /> */}
                  <Select
                    allowClear
                    value={filters.orderState}
                    placeholder={t("purchase.orderState")}
                    style={{ width: 130 }}
                    onChange={(value) => {
                      const nextFilters = {
                        ...filters,
                        orderState: value,
                      };
                      onFiltersChange?.(nextFilters);
                    }}
                    options={[
                      {
                        label: t("purchase.purchased"),
                        value: PurchaseOrderStateEnum.PAYED,
                      },
                      {
                        label: t("layout.purDone"),
                        value: PurchaseOrderStateEnum.DONE,
                      },
                    ]}
                  />
                  <div style={{ width: 120 }}>
                    <SupplierSelect
                      allowClear
                      placeholder={t("purchase.plSupplier")}
                      value={filters.supplierId || undefined}
                      onChange={(value) => {
                        const nextFilters = {
                          ...filters,
                          supplierId: value ?? "",
                        };
                        onFiltersChange?.(nextFilters);
                      }}
                    />
                  </div>
                  <RangePicker
                    value={[filters.startTime ?? null, filters.endTime ?? null]}
                    onChange={(value) => {
                      const nextFilters = {
                        ...filters,
                        startTime: value?.[0] ?? null,
                        endTime: value?.[1] ?? null,
                      };
                      onFiltersChange?.(nextFilters);
                    }}
                  />
                  <Tooltip title={t("purchase.searchCode")}>
                    <Input
                      placeholder={t("purchase.searchCode")}
                      style={{ width: 160 }}
                      value={filters.keyword ?? ""}
                      onChange={(e) =>
                        onFiltersChange?.({
                          ...filters,
                          keyword: e.target.value.trim(),
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.code === "Enter") {
                          tableRef.current?.resetTablePage();
                          onSearch?.(filters);
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={t("purchase.search")}>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={() => {
                        tableRef.current?.resetTablePage();
                        onSearch?.(filters);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={t("purchase.clearFilters")}>
                    <Button
                      type="primary"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        tableRef.current?.resetTablePage();
                        onClearFilters?.();
                      }}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
            }
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              onChange: (keys, rows) =>
                onSelectedRowKeysChange?.(keys, rows as PurchaseOrderItem[]),
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default BatchCompleteTable;
